import { Children, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./commerce.css";

export function CommerceDialog({ title, subtitle, onClose, footer, children, className = "" }) {
  const titleId = useId();
  const dialogRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const previous = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.querySelector("button")?.focus();
    return () => {
      document.body.style.overflow = overflow;
      if (previous?.isConnected) previous.focus();
    };
  }, []);
  function handleKey(e) {
    if ([...dialogRef.current.querySelectorAll('[role="dialog"]')].some(el => el.getClientRects().length)) return;
    if (e.target.closest('[role="dialog"]') !== dialogRef.current) return;
    if (e.key === "Escape") { e.stopPropagation(); closeRef.current(); }
    if (e.key !== "Tab") return;
    const fields = [...dialogRef.current.querySelectorAll('button, input, select, textarea, a[href], summary, [tabindex="0"]')]
      .filter(el => !el.disabled && el.tabIndex >= 0 && el.getClientRects().length);
    const first = fields[0], last = fields[fields.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
  }
  return createPortal(
    <div className="commerce-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <section ref={dialogRef} className={`commerce-dialog ${className}`} role="dialog" aria-modal="true" aria-labelledby={titleId} onKeyDown={handleKey}>
        <header className="commerce-dialog-header">
          <div><h2 id={titleId}>{title}</h2>{subtitle && <p>{subtitle}</p>}</div>
          <button className="commerce-close" aria-label="Fechar" onClick={onClose}>×</button>
        </header>
        <div className="commerce-dialog-body">{children}</div>
        {footer && <footer className="commerce-dialog-footer">{footer}</footer>}
      </section>
    </div>, document.body
  );
}

export function CommerceTabs({ children }) {
  const panels = Children.toArray(children);
  const [active, setActive] = useState(0);
  const id = useId();
  const refs = useRef([]);
  function navigate(e, index) {
    let next;
    if (e.key === "ArrowRight") next = (index + 1) % panels.length;
    if (e.key === "ArrowLeft") next = (index + panels.length - 1) % panels.length;
    if (e.key === "Home") next = 0;
    if (e.key === "End") next = panels.length - 1;
    if (next === undefined) return;
    e.preventDefault(); setActive(next); refs.current[next]?.focus();
  }
  return <>
    <div className="commerce-tabs" role="tablist" aria-label="Seções">
      {panels.map((panel, i) => <button key={panel.key} ref={el => { refs.current[i] = el; }} role="tab" id={`${id}-tab-${i}`} aria-controls={`${id}-panel-${i}`} aria-selected={active === i} tabIndex={active === i ? 0 : -1} onClick={() => setActive(i)} onKeyDown={e => navigate(e, i)}>{panel.props.label}</button>)}
    </div>
    {panels.map((panel, i) => <div key={panel.key} className="commerce-tab-panel" role="tabpanel" id={`${id}-panel-${i}`} aria-labelledby={`${id}-tab-${i}`} hidden={active !== i} tabIndex={0}>{panel}</div>)}
  </>;
}
export function CommercePanel({ children }) { return children; }